/**
 * Toast texts of the series writes (#2119): what a recurrence change added, removed or
 * skipped, and which days a create skipped. The toast names the result instead of a bare
 * "Series updated".
 */
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { formatDateDisplay, formatList } from '@/lib/format';
import type { CreateShiftResponse, SeriesUpdateSummary } from '@/types/shifts';

export interface ShiftToast {
  title: string;
  description?: string;
}

/** Joins ISO days as dd.MM.yyyy. */
export function formatDayList(days: string[] | undefined, locale: string): string {
  return (days ?? []).map((day) => formatDateDisplay(day, locale)).join(', ');
}

const messages = {
  updated: msg({ id: 'shifts.toast.seriesUpdateSuccess', message: 'Series updated' }),
  extended: msg({
    id: 'shifts.toast.seriesExtendedBy',
    message: '{count, plural, one {Series extended by # shift} other {Series extended by # shifts}}',
  }),
  shortened: msg({
    id: 'shifts.toast.seriesShortenedBy',
    message: '{count, plural, one {Series shortened by # shift} other {Series shortened by # shifts}}',
  }),
  nothingAdded: msg({ id: 'shifts.toast.seriesNothingAdded', message: 'Series not extended: every new day collides' }),
  reshaped: msg({ id: 'shifts.toast.seriesReshaped', message: 'Series pattern changed' }),
  added: msg({
    id: 'shifts.toast.seriesAddedDrafts',
    message:
      '{count, plural, one {# day added as a draft. Publish it so the employee sees it.} other {# days added as drafts. Publish them so the employee sees them.}}',
  }),
  removed: msg({
    id: 'shifts.toast.seriesRemoved',
    message:
      '{count, plural, one {# shift removed; if it was published, the employee was notified.} other {# shifts removed; if they were published, the employee was notified.}}',
  }),
  keptPublished: msg({
    id: 'shifts.toast.seriesKeptPublished',
    message:
      '{count, plural, one {# published shift no longer fits the pattern and was kept.} other {# published shifts no longer fit the pattern and were kept.}}',
  }),
  skipped: msg({
    id: 'shifts.toast.seriesSkipped',
    message: 'Skipped because the employee already works or is absent: {days}',
  }),
  status: msg({
    id: 'shifts.toast.seriesStatusChanged',
    message: '{count, plural, one {Status of # shift changed.} other {Status of # shifts changed.}}',
  }),
  created: msg({ id: 'shifts.toast.createSuccess', message: 'Shift created' }),
  createdSeries: msg({
    id: 'shifts.toast.createdSeriesSkipped',
    message: '{count, plural, one {# shift created} other {# shifts created}}',
  }),
  createdFor: msg({
    id: 'shifts.toast.createdFor',
    message: '{count, plural, one {# shift created for {names}} other {# shifts created for {names}}}',
  }),
  skippedPair: msg({ id: 'shifts.toast.skippedPair', message: '{name}: {days}' }),
  skippedPeople: msg({
    id: 'shifts.toast.skippedPeople',
    message: 'Not created because they already work or are absent: {pairs}',
  }),
};

/**
 * The toast of a series edit; summary is absent for a "this shift only" edit. The title
 * names what changed: the shifts added or removed, not the series length, which skipped
 * days would overstate.
 */
export function seriesUpdateToast(summary: SeriesUpdateSummary | undefined, i18n: I18n): ShiftToast {
  if (!summary) return { title: i18n._(messages.updated) };
  const lines: string[] = [];
  const added = summary.added_count ?? 0;
  const removed = summary.removed_count ?? 0;
  const kept = summary.kept_published_count ?? 0;
  const skipped = summary.skipped_dates ?? [];
  const statusChanged = summary.status_changed_count ?? 0;
  if (added > 0) lines.push(i18n._(messages.added, { count: added }));
  if (removed > 0) lines.push(i18n._(messages.removed, { count: removed }));
  if (kept > 0) lines.push(i18n._(messages.keptPublished, { count: kept }));
  if (skipped.length > 0) {
    lines.push(i18n._(messages.skipped, { days: formatDayList(skipped, i18n.locale) }));
  }
  if (statusChanged > 0) lines.push(i18n._(messages.status, { count: statusChanged }));

  let title = i18n._(messages.updated);
  if (summary.regenerated) {
    if (added > 0 && removed === 0) {
      title = i18n._(messages.extended, { count: added });
    } else if (removed > 0 && added === 0) {
      title = i18n._(messages.shortened, { count: removed });
    } else if (added === 0 && removed === 0 && skipped.length > 0) {
      title = i18n._(messages.nothingAdded);
    } else {
      title = i18n._(messages.reshaped);
    }
  }
  return { title, description: lines.length > 0 ? lines.join(' ') : undefined };
}

/**
 * The toast of a create; a series with skipped days names them. A multi-employee create
 * (#2121) names the people and the skipped (person, days) pairs.
 */
export function createShiftToast(
  data: Pick<CreateShiftResponse, 'created_count' | 'skipped_dates' | 'shifts' | 'skipped'>,
  i18n: I18n,
  nameOf: (employeeId: string) => string = (id) => id
): ShiftToast {
  if (data.shifts?.length) {
    const names = formatList(data.shifts.map((s) => nameOf(s.employee_id)), i18n.locale);
    const pairs = (data.skipped ?? [])
      .map((pair) => i18n._(messages.skippedPair, { name: nameOf(pair.employee_id), days: formatDayList(pair.dates, i18n.locale) }))
      .join(' · ');
    return {
      title: i18n._(messages.createdFor, { count: data.created_count ?? 0, names }),
      description: pairs ? i18n._(messages.skippedPeople, { pairs }) : undefined,
    };
  }
  const createdCount = data.created_count;
  const skippedDates = data.skipped_dates;
  if (!skippedDates || skippedDates.length === 0) return { title: i18n._(messages.created) };
  return {
    title: i18n._(messages.createdSeries, { count: createdCount ?? 0 }),
    description: i18n._(messages.skipped, { days: formatDayList(skippedDates, i18n.locale) }),
  };
}
